import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ActionButton } from '@/components/ui/action-button';
import { 
  Users, 
  Pencil, 
  Trash2, 
  UserPlus, 
  ChevronDown, 
  ChevronUp,
  Mail,
  X,
  Check
} from 'lucide-react';
import { useGroupsManagement, SharingGroup } from '@/hooks/useGroupsManagement';
import { EditGroupDialog } from './EditGroupDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useSearchUsers } from '@/hooks/useShareCaso';

interface GroupCardProps {
  group: SharingGroup;
}

export function GroupCard({ group }: GroupCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addMemberEmail, setAddMemberEmail] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  
  const { deleteGroup, removeMember, addMember } = useGroupsManagement();
  const { data: searchResults } = useSearchUsers(addMemberEmail);

  const handleDeleteGroup = async () => {
    await deleteGroup.mutateAsync(group.id);
    setDeleteDialogOpen(false);
  };

  const handleRemoveMember = async (userId: string) => {
    await removeMember.mutateAsync({ groupId: group.id, userId });
  };

  const handleAddMember = async () => {
    if (selectedUserIds.length === 0) return;
    
    await addMember.mutateAsync({ 
      groupId: group.id, 
      userIds: selectedUserIds 
    });
    
    setAddMemberEmail('');
    setSelectedUserIds([]);
    setShowAddMember(false);
  };

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const isValidEmail = (email: string) => {
    return email.includes('@') && email.includes('.');
  };

  const isUserInGroup = (userId: string) => {
    return group.members.some(m => m.user_id === userId);
  };

  return (
    <>
      <Card className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">{group.name}</h3>
              <Badge variant="secondary" className="text-xs">
                {group.member_count} {group.member_count === 1 ? 'membro' : 'membros'}
              </Badge>
            </div>
            {group.description && (
              <p className="text-sm text-muted-foreground mb-2">{group.description}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <ActionButton
              icon={Pencil}
              label="Editar"
              variant="accept"
              onClick={() => setEditDialogOpen(true)}
              className="h-9"
            />
            <ActionButton
              icon={Trash2}
              label="Remover"
              variant="reject"
              onClick={() => setDeleteDialogOpen(true)}
              className="h-9"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">Membros</h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddMember(!showAddMember)}
              >
                <UserPlus className="h-3 w-3 mr-1" />
                Adicionar
              </Button>
            </div>

            {showAddMember && (
              <div className="space-y-3 mb-3">
                <Input
                  placeholder="Digite o e-mail completo do usuário..."
                  value={addMemberEmail}
                  onChange={(e) => setAddMemberEmail(e.target.value)}
                  type="email"
                />

                {isValidEmail(addMemberEmail) && searchResults && searchResults.length > 0 && (
                  <ScrollArea className="h-[150px] rounded-md border p-2">
                    <div className="space-y-2">
                      {searchResults.map((user) => {
                        const isSelected = selectedUserIds.includes(user.id);
                        const inGroup = isUserInGroup(user.id);
                        
                        return (
                          <button
                            key={user.id}
                            onClick={() => !inGroup && handleToggleUser(user.id)}
                            disabled={inGroup}
                            className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
                              inGroup 
                                ? 'opacity-50 cursor-not-allowed' 
                                : isSelected
                                ? 'bg-primary/10 hover:bg-primary/20'
                                : 'hover:bg-accent'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={user.avatar_url || undefined} />
                                <AvatarFallback>
                                  {user.display_name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="text-left">
                                <p className="text-sm font-medium">
                                  {user.display_name || user.email}
                                </p>
                                {user.display_name && (
                                  <p className="text-xs text-muted-foreground">{user.email}</p>
                                )}
                              </div>
                            </div>
                            {inGroup ? (
                              <Badge variant="secondary" className="text-xs">Já no grupo</Badge>
                            ) : isSelected ? (
                              <Check className="h-4 w-4 text-primary" />
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}

                {isValidEmail(addMemberEmail) && searchResults && searchResults.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-2">
                    Nenhum usuário encontrado com este e-mail
                  </p>
                )}

                {selectedUserIds.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedUserIds.map((userId) => {
                      const user = searchResults?.find(u => u.id === userId);
                      if (!user) return null;
                      
                      return (
                        <Badge key={userId} variant="secondary" className="gap-1">
                          {user.display_name || user.email}
                          <X 
                            className="h-3 w-3 cursor-pointer hover:text-destructive" 
                            onClick={() => handleToggleUser(userId)}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                )}

                <Button
                  onClick={handleAddMember}
                  disabled={selectedUserIds.length === 0 || addMember.isPending}
                  className="w-full"
                  size="sm"
                >
                  Adicionar {selectedUserIds.length > 0 && `(${selectedUserIds.length})`}
                </Button>
              </div>
            )}

            {group.members.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-2">
                Nenhum membro ainda
              </p>
            ) : (
              <div className="space-y-2">
                {group.members.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-accent">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={member.avatar_url || undefined} />
                        <AvatarFallback>
                          {member.display_name?.[0]?.toUpperCase() || member.email[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">
                          {member.display_name || member.email}
                        </p>
                        {member.display_name && (
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                        )}
                      </div>
                    </div>
                    <ActionButton
                      icon={X}
                      label="Remover"
                      variant="reject"
                      onClick={() => handleRemoveMember(member.user_id)}
                      className="h-8"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      <EditGroupDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        group={group}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Grupo</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o grupo "{group.name}"? Esta ação não pode ser desfeita.
              Todos os compartilhamentos associados a este grupo serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGroup}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
